import React, { useEffect, useState } from 'react';
import { useModal } from 'react-modal-hook';
import { useDispatch, useSelector } from 'react-redux';
import { AlertContent, ModalFactory } from 'components';
import { useFeatureFlags } from 'contexts/featureFlags';
import { selectAPIRequestStatus, selectIsLoadingAPIRequest } from 'store/ApiClient';
import { getPosCategoriesRequest, getPosEmployeesRequest, POS } from 'store/Collections';
import { IStore } from 'store/types';
import { FeatureFlags, IFormCalculatorRule, IFormValues, RequestStatus } from 'types';
import { capitalize, sortAlphaNumerically } from 'utils';
import { AddButton, TipCalculationsContainer } from '../../../../styles';
import { PosLoader } from '../../../shared/PosLoader';
import { FeatureUnavailableTooltip, MoreInfoButton, PermissionTooltip, TipCalculationsDescription } from '../../../shared/styles';
import { CreateTipShare } from '../modals/CreateTipShare';
import { TipSharingInfoModal } from '../modals/TipSharingInfoModal';
import { TipShareItem } from '../TipShareItem';

interface ITipSharesFormProps {
  siteId: string;
  activeRules?: IFormValues;
  isLoading?: boolean;
  onConfirm: (values: IFormValues, onSuccess?: () => void) => void;
  editPermission?: boolean;
  tabNav: React.ReactNode;
}

const TipSharesForm: React.FC<ITipSharesFormProps> = ({ siteId, activeRules, isLoading, onConfirm, editPermission, tabNav }) => {
  const dispatch = useDispatch();
  const isLoadingPosCategories = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, POS.GET_POS_CATEGORIES_REQUEST));
  const isLoadingPosEmployees = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, POS.GET_POS_EMPLOYEES_REQUEST));
  const getPosCategoriesStatus = useSelector((state: IStore) => selectAPIRequestStatus(state, POS.GET_POS_CATEGORIES_REQUEST));
  const getPosEmployeesStatus = useSelector((state: IStore) => selectAPIRequestStatus(state, POS.GET_POS_EMPLOYEES_REQUEST));
  const [selectedRule, setSelectedRule] = useState<IFormCalculatorRule>();
  const [showLoader, setShowLoader] = useState(false);
  const tipCalculatorRulesEnabled = useFeatureFlags()[FeatureFlags.TIP_CALCULATOR_RULES]?.enabled;

  const [showCreateTipShare, hideCreateTipShare] = useModal(
    () => <CreateTipShare hideModal={hideCreateTipShare} initialValues={activeRules} onConfirm={onConfirm} siteId={siteId} isLoading={isLoading} selectedRule={selectedRule} />,
    [activeRules, isLoading, onConfirm, selectedRule]
  );

  const [showPosLoader, hidePosLoader] = useModal(() => <PosLoader />, []);

  const loadPosCategories = () => {
    showPosLoader();
    dispatch(getPosCategoriesRequest(siteId));
    dispatch(getPosEmployeesRequest(siteId));
    setShowLoader(true);
  };

  const onEditTipShare = (rule: IFormCalculatorRule) => {
    setSelectedRule(rule);
    loadPosCategories();
  };

  const onCreateTipShare = () => {
    setSelectedRule(undefined);
    loadPosCategories();
  };

  useEffect(() => {
    if (!isLoadingPosCategories && !isLoadingPosEmployees && showLoader) {
      hidePosLoader();
      setShowLoader(false);

      if (getPosCategoriesStatus === RequestStatus.SUCCESS && getPosEmployeesStatus === RequestStatus.SUCCESS) {
        showCreateTipShare();
      }
    }
  }, [isLoadingPosCategories, isLoadingPosEmployees]);

  const [showModal, hideModal] = useModal(() => <TipSharingInfoModal hideModal={hideModal} />, []);

  const [showDeleteModal, hideDeleteModal] = useModal(() => {
    const onDelete = () => {
      const filteredRules = activeRules.rules.filter((rule) => rule.id !== selectedRule.id);
      onConfirm({ ...activeRules, rules: filteredRules }, hideDeleteModal);
    };

    return (
      <ModalFactory customModal onClose={hideDeleteModal} isActive>
        <AlertContent
          title={`Remove ${capitalize(selectedRule.contributor.name)} Share?`}
          message="This will delete all of the rules associated with this tip share and cannot be undone."
          leftActionLabel="Cancel"
          rightActionLabel="Remove"
          onClose={hideDeleteModal}
          onLeftAction={hideDeleteModal}
          onRightAction={onDelete}
          isLoading={isLoading}
          rightActionAppearance="destructive"
        />
      </ModalFactory>
    );
  }, [activeRules, selectedRule, isLoading, onConfirm]);

  const handleDelete = (rule: IFormCalculatorRule) => {
    setSelectedRule(rule);
    showDeleteModal();
  };

  return (
    <TipCalculationsContainer data-cy="tip-shares-form">
      {tabNav}
      <TipCalculationsDescription size="xsmall">
        {'Configure your tip sharing policy by creating rules based on jobs or rules for specific check owners. '}
        <MoreInfoButton onClick={showModal}>Click here to learn more.</MoreInfoButton>
      </TipCalculationsDescription>
      {activeRules.rules
        .filter((rule) => !!rule?.sharePolicy && !rule.default)
        .sort(sortAlphaNumerically('contributor.name'))
        .map((rule) => (
          <TipShareItem key={rule.id} tipShare={rule} onEdit={() => onEditTipShare(rule)} onDelete={() => handleDelete(rule)} disabled={!editPermission || !tipCalculatorRulesEnabled} />
        ))}
      <AddButton
        appearance="white"
        icon="Plus"
        label="Create Tip Share"
        data-cy="create-tip-share-button"
        onClick={onCreateTipShare}
        disabled={!editPermission || !tipCalculatorRulesEnabled}
        tooltipId={!editPermission ? 'no-edit-permission' : 'feature-unavailable'}
      />
      {!editPermission ? <PermissionTooltip type="edit" /> : !tipCalculatorRulesEnabled && <FeatureUnavailableTooltip />}
    </TipCalculationsContainer>
  );
};

export default TipSharesForm;
